import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { notificationsApi } from '../../lib/api';
import { Bell, MessageSquare, Check } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const NotificationBell = () => {
  const { language } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await notificationsApi.unreadCount();
      setUnreadCount(res.data.count || 0);
    } catch {}
  }, []);

  // Poll every 30s
  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsApi.list();
      setNotifications(res.data?.slice(0, 8) || []);
    } catch {}
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setUnreadCount(0);
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch {}
  };

  const getTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (language === 'ar') {
      if (mins < 60) return `منذ ${mins} د`;
      if (hrs < 24) return `منذ ${hrs} س`;
      return `منذ ${days} ي`;
    }
    if (mins < 60) return `${mins} min`;
    if (hrs < 24) return `${hrs}h`;
    return `${days}j`;
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px] px-1" data-testid="notification-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm text-slate-900">
            {language === 'fr' ? 'Notifications' : 'الإشعارات'}
          </span>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="mark-all-read">
              <Check className="w-3 h-3" />
              {language === 'fr' ? 'Tout marquer lu' : 'تحديد الكل كمقروء'}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              {language === 'fr' ? 'Aucune notification' : 'لا توجد إشعارات'}
            </div>
          ) : (
            notifications.map(n => (
              <DropdownMenuItem key={n.id} asChild className="p-0">
                <Link
                  to={n.link || '/dashboard'}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer ${!n.read ? 'bg-blue-50/50' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${!n.read ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                      {language === 'ar' ? n.title_ar : n.title}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">
                      {language === 'ar' ? n.message_ar : n.message}
                    </p>
                    <p className="text-[10px] text-slate-300 mt-1">{getTimeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />}
                </Link>
              </DropdownMenuItem>
            ))
          )}
        </div>
        <Link
          to="/dashboard"
          className="block text-center text-xs text-blue-600 hover:underline py-2.5 border-t"
          onClick={() => setOpen(false)}
          data-testid="see-all-notifications"
        >
          {language === 'fr' ? 'Voir tout' : 'عرض الكل'}
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
